import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useCallback,
} from 'react';
import { motion, useMotionValue, PanInfo, AnimatePresence, HTMLMotionProps } from 'motion/react';
import { Paper, Box, Typography } from '@mui/material';
import { Check as CheckIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { ActionCommand, ActionCommandType } from '../../../types';

export interface SwipeableCardProps extends HTMLMotionProps<'div'> {
  id: string;
  onCommand: (command: ActionCommand) => void;
  customIndex?: number;
  children: React.ReactNode;
}

// Define the imperative handle type that combines animation methods with HTMLDivElement properties
export interface SwipeableCardHandle extends Partial<HTMLDivElement> {
  animateAccept: (callback?: () => void) => void;
  animateReject: (callback?: () => void) => void;
  resetCard: () => void; // Add reset method
  domElement: HTMLDivElement | null; // Add this explicit property for DOM access
}

/**
 * Base component for all swipeable card types with animations
 */
const SwipeableCard = forwardRef<SwipeableCardHandle, SwipeableCardProps>(
  ({ id, onCommand, customIndex = 0, children, ...props }, ref) => {
    // State for tracking animations and interactions
    const [exitAnimation, setExitAnimation] = useState<{
      x: number;
      opacity: number;
    } | null>(null);
    const [dragX, setDragX] = useState(0);

    // Refs for measurements
    const innerCardRef = useRef<HTMLDivElement>(null);
    const cardWidth = useRef<number>(0);
    const rootDivRef = useRef<HTMLDivElement>(null);
    const timeoutRef = useRef<number | undefined>(undefined);

    // Framer Motion values for swipe animation
    const x = useMotionValue(0);

    // Subscribe to x changes to update dragX state in real-time
    useEffect(() => {
      const unsubscribe = x.on('change', latest => {
        setDragX(latest);
      });
      return unsubscribe;
    }, [x]);

    // Measure card on mount
    useEffect(() => {
      if (innerCardRef.current) {
        cardWidth.current = innerCardRef.current.offsetWidth;
      }
    }, []);

    // Cleanup timeouts on unmount
    useEffect(() => {
      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    }, []);

    // Memoized styling for accept action (left side)
    const acceptStyles = useMemo(
      () => ({
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        pl: dragX > 0 ? 3 : 0,
        flex: 1,
        bgcolor: 'success.main',
        color: 'white',
        width: dragX > 20 ? '100%' : '0%',
        flexBasis: dragX > 20 ? '100%' : '0%',
        transition: 'width 0.2s ease',
        overflow: 'hidden',
      }),
      [dragX],
    );

    // Memoized styling for reject action (right side)
    const rejectStyles = useMemo(
      () => ({
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        pr: dragX < 0 ? 3 : 0,
        flex: 1,
        bgcolor: 'error.main',
        color: 'white',
        width: dragX < -20 ? '100%' : '0%',
        flexBasis: dragX < -20 ? '100%' : '0%',
        transition: 'width 0.2s ease',
        overflow: 'hidden',
      }),
      [dragX],
    );

    // Animate card exit when accepting
    const animateAccept = useCallback(
      (callback?: () => void) => {
        setExitAnimation({ x: cardWidth.current * 2, opacity: 0 });

        // Clear any existing timeout
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }

        // Store the timeout ID for cleanup
        timeoutRef.current = window.setTimeout(() => {
          if (callback) {
            callback();
          } else {
            onCommand({
              type: ActionCommandType.ACCEPT,
            });
          }
          timeoutRef.current = undefined;
        }, 300);
      },
      [onCommand],
    );

    // Animate card exit when rejecting
    const animateReject = useCallback(
      (callback?: () => void) => {
        setExitAnimation({ x: -cardWidth.current * 2, opacity: 0 });

        // Clear any existing timeout
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }

        // Store the timeout ID for cleanup
        timeoutRef.current = window.setTimeout(() => {
          if (callback) {
            callback();
          } else {
            onCommand({
              type: ActionCommandType.REJECT,
            });
          }
          timeoutRef.current = undefined;
        }, 300);
      },
      [onCommand],
    );

    // Reset card to original state
    const resetCard = useCallback(() => {
      // Clear any pending animations
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = undefined;
      }

      // Reset drag position and exit animation
      setDragX(0);
      setExitAnimation(null);
      x.set(0);
    }, [x]);

    // Handle drag end (swipe gesture)
    const handleDragEnd = useCallback(
      (event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
        const { offset, velocity } = info;

        // Threshold for triggering action - 1/2 of card width or high velocity
        const threshold = cardWidth.current / 2;

        // Reject if swiped left with enough force or distance
        if (offset.x < -threshold || velocity.x < -500) {
          animateReject();
        }
        // Accept if swiped right with enough force or distance
        else if (offset.x > threshold || velocity.x > 500) {
          animateAccept();
        } else {
          // Reset drag position if not accepting or rejecting
          setDragX(0);
        }
      },
      [animateAccept, animateReject],
    );

    // Expose methods via ref along with DOM element properties
    useImperativeHandle(
      ref,
      () => ({
        animateAccept,
        animateReject,
        resetCard,
        domElement: rootDivRef.current, // Explicitly provide the DOM element
      }),
      [rootDivRef.current, animateAccept, animateReject, resetCard],
    );

    // Animation variants with staggering support - memoized to prevent recreations
    const cardVariants = useMemo(
      () => ({
        hidden: {
          opacity: 0,
          x: 300,
          height: 'auto',
        },
        visible: {
          opacity: 1,
          x: 0,
          height: 'auto',
          transition: {
            type: 'spring' as const,
            damping: 17, // Lower damping for more bounce
            stiffness: 120, // Adjusted stiffness for better bounce
            mass: 0.9, // Slightly lighter mass for quicker movement
            delay: customIndex * 0.1, // Staggered delay based on index
            bounce: 0.25, // Add a subtle bounce
          },
        },
        exit: {
          opacity: 0,
          height: 0,
          transition: {
            height: { delay: 0.2, duration: 0.3 },
            opacity: { duration: 0.2 },
          },
        },
      }),
      [customIndex],
    );

    return (
      <motion.div
        variants={cardVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        // layout="size" // TODO: Uncomment for layout animations, but figure out the skewing thing first
        // layoutId={`card-${id}`}
        ref={rootDivRef}
        {...props}
      >
        <Paper
          elevation={1}
          sx={{
            p: 0,
            mb: 2,
            backgroundColor: 'background.paper',
            borderRadius: 2,
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            position: 'relative',
            border: 1,
            borderColor: 'borderColors.primary',
          }}
        >
          {/* Background actions that get revealed when swiping */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: 0,
              right: 0,
              display: 'flex',
              zIndex: 0,
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            {/* Left side action (accept) */}
            <Box sx={acceptStyles}>
              <CheckIcon fontSize="medium" />
              <Typography variant="button" sx={{ ml: 1 }}>
                Accept
              </Typography>
            </Box>

            {/* Right side action (reject) */}
            <Box sx={rejectStyles}>
              <Typography variant="button" sx={{ mr: 1 }}>
                Reject
              </Typography>
              <DeleteIcon fontSize="medium" />
            </Box>
          </Box>

          {/* Card with swipe gesture */}
          <motion.div
            ref={innerCardRef}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.6}
            onDragEnd={handleDragEnd}
            animate={exitAnimation || { x: 0, opacity: 1 }}
            transition={{ duration: 0.3 }}
            style={{
              x,
              zIndex: 1,
              position: 'relative',
              background: 'white',
              boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
            }}
            initial={{ opacity: 1, y: 0 }}
          >
            {children}
          </motion.div>
        </Paper>
      </motion.div>
    );
  },
);

// Add display name for better debugging
SwipeableCard.displayName = 'SwipeableCard';

export default SwipeableCard;
